import { useCallback, useState } from 'react';
import { trackingService } from '../../../services/tracking';
import { useTrackingStore } from '../../../stores';
import type { SecurityPinType, SecurityPinVerifyMethod, SecurityPinVerifyResponse } from '../../../types/tracking';

export type SecurityPinVerifyErrorKind =
    | 'invalid_pin'
    | 'invalid_qr'
    | 'no_active_pin'
    | 'locked'
    | 'forbidden'
    | 'rate_limited'
    | 'network';

export interface SecurityPinVerifyError {
    kind: SecurityPinVerifyErrorKind;
    message: string;
    code?: string;
    remainingAttempts?: number | null;
}

export interface SecurityPinVerifySuccess {
    ok: true;
    method: SecurityPinVerifyMethod | string | null;
    verifiedAt: string | null;
}

export interface SecurityPinVerifyFailure {
    ok: false;
    error: SecurityPinVerifyError;
}

export type SecurityPinVerifyResult = SecurityPinVerifySuccess | SecurityPinVerifyFailure;

interface ApiErrorShape {
    message?: string;
    code?: string;
    errors?: Record<string, unknown>;
    status?: number;
}

function asApiError(error: unknown): ApiErrorShape {
    if (typeof error === 'object' && error !== null) {
        return error as ApiErrorShape;
    }

    return {};
}

function readRemainingAttempts(errors?: Record<string, unknown>): number | null {
    const raw = errors?.['remaining_attempts'];

    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return Math.max(0, Math.floor(raw));
    }

    if (typeof raw === 'string') {
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : null;
    }

    return null;
}

function mapError(error: unknown, usingQr: boolean): SecurityPinVerifyError {
    const meta = asApiError(error);
    const code = typeof meta.code === 'string' ? meta.code : undefined;
    const remainingAttempts = readRemainingAttempts(meta.errors);

    if (code === 'PIN_LOCKED_REGENERATED') {
        return {
            kind: 'locked',
            message:
                meta.message
                ?? 'Demasiados intentos. Se generó un nuevo PIN y se notificó al cliente.',
            code,
            remainingAttempts: 0,
        };
    }

    if (code === 'NO_ACTIVE_PIN') {
        return {
            kind: 'no_active_pin',
            message: meta.message ?? 'No hay un PIN activo para validar.',
            code,
            remainingAttempts,
        };
    }

    if (code === 'INVALID_QR_SIGNATURE') {
        return {
            kind: 'invalid_qr',
            message: meta.message ?? 'El código QR no es válido o ya expiró.',
            code,
            remainingAttempts,
        };
    }

    if (code === 'INVALID_PIN') {
        return {
            kind: 'invalid_pin',
            message: meta.message ?? 'PIN incorrecto.',
            code,
            remainingAttempts,
        };
    }

    if (code === 'NOT_OPERATOR' || code === 'NOT_ALLOWED' || code === 'CONTRACT_NOT_FOUND') {
        return {
            kind: 'forbidden',
            message: meta.message ?? 'No puedes validar este PIN.',
            code,
        };
    }

    if (code === 'RATE_LIMITED' || meta.status === 429) {
        return {
            kind: 'rate_limited',
            message: meta.message ?? 'Demasiados intentos. Espera unos segundos.',
            code,
        };
    }

    return {
        kind: usingQr ? 'invalid_qr' : 'network',
        message: meta.message ?? 'No se pudo validar el PIN.',
        code,
        remainingAttempts,
    };
}

function readResponse(response: SecurityPinVerifyResponse): SecurityPinVerifySuccess {
    return {
        ok: true,
        method: response.data?.method ?? null,
        verifiedAt: response.data?.verified_at ?? null,
    };
}

export interface UseSecurityPinVerificationResult {
    verifyByPin: (pin: string) => Promise<SecurityPinVerifyResult>;
    verifyByQrToken: (token: string) => Promise<SecurityPinVerifyResult>;
    isSubmitting: boolean;
    lastError: SecurityPinVerifyError | null;
    remainingAttempts: number | null;
    isLocked: boolean;
    reset: () => void;
}

export function useSecurityPinVerification(
    contractId: number,
    type: SecurityPinType,
): UseSecurityPinVerificationResult {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastError, setLastError] = useState<SecurityPinVerifyError | null>(null);
    const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
    const [isLocked, setIsLocked] = useState(false);

    const refreshTracking = useTrackingStore((state) => state.refreshState);

    const submit = useCallback(
        async (payload: { pin?: string; qr_token?: string }, usingQr: boolean): Promise<SecurityPinVerifyResult> => {
            setIsSubmitting(true);

            try {
                const response = await trackingService.verifySecurityPin(contractId, type, payload);
                const success = readResponse(response);
                setLastError(null);
                setRemainingAttempts(null);
                setIsLocked(false);

                try {
                    await refreshTracking?.(contractId);
                } catch {
                    // El refresh no debe romper el flujo de UI.
                }

                return success;
            } catch (requestError) {
                const mapped = mapError(requestError, usingQr);
                setLastError(mapped);

                if (mapped.kind === 'locked') {
                    setIsLocked(true);
                    setRemainingAttempts(0);
                } else if (typeof mapped.remainingAttempts === 'number') {
                    setRemainingAttempts(mapped.remainingAttempts);
                }

                return { ok: false, error: mapped };
            } finally {
                setIsSubmitting(false);
            }
        },
        [contractId, refreshTracking, type],
    );

    const verifyByPin = useCallback(
        (pin: string) => submit({ pin: pin.trim() }, false),
        [submit],
    );

    const verifyByQrToken = useCallback(
        (token: string) => submit({ qr_token: token }, true),
        [submit],
    );

    const reset = useCallback(() => {
        setLastError(null);
        setRemainingAttempts(null);
        setIsLocked(false);
    }, []);

    return {
        verifyByPin,
        verifyByQrToken,
        isSubmitting,
        lastError,
        remainingAttempts,
        isLocked,
        reset,
    };
}
